import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { events } from '@/mocks/events';
import EventCard from '@/components/feature/EventCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import usePageMeta from '@/hooks/usePageMeta';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const event = events.find((item) => item.id === id);

  const related = useMemo(() => {
    if (!event) return [];
    const sameCategory = events.filter((item) => item.id !== event.id && item.categoryId === event.categoryId);
    const others = events.filter((item) => item.id !== event.id && item.categoryId !== event.categoryId);
    return [...sameCategory, ...others].slice(0, 3);
  }, [event]);

  const topHot = useMemo(() => [...events].sort((a, b) => b.heat - a.heat).slice(0, 5), []);

  const jsonLd = useMemo(() => {
    if (!event) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: event.title,
      description: event.summary,
      articleSection: event.category,
      datePublished: event.publishedAt,
      dateModified: event.publishedAt,
      inLanguage: 'zh-CN',
      keywords: event.tags.join(','),
      author: { '@type': 'Organization', name: '高校吃瓜社' },
      publisher: { '@type': 'Organization', name: '高校吃瓜社' },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '事件合集', item: '/events' },
          { '@type': 'ListItem', position: 3, name: event.title, item: `/events/${event.id}` },
        ],
      },
    };
  }, [event]);

  usePageMeta({
    title: event
      ? `${event.title}｜${event.category}·高校吃瓜事件 - 高校吃瓜社`
      : '事件未找到 - 高校吃瓜社',
    description: event
      ? `${event.summary} 来自${event.campus}的校园话题，附完整事件时间线与相关推荐。`
      : '抱歉，没有找到对应的事件内容。',
    keywords: event
      ? `高校吃瓜,高校吃瓜事件,${event.category},${event.tags.join(',')}`
      : '高校吃瓜,高校吃瓜事件',
    canonicalPath: event ? `/events/${event.id}` : '/events',
    ogType: 'article',
    ogImage: event?.cover,
    jsonLd,
  });

  if (!event) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-20 text-center md:px-6 md:py-28">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-3xl leading-none" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
          这起事件暂时找不到了
        </h1>
        <p className="mt-3 text-sm text-foreground-600">它可能已合并到其他话题里，先去合集看看其他正在围观的事吧。</p>
        <Link
          to="/events"
          className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回事件合集
          <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true"></i>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-12 lg:px-10">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '事件合集', to: '/events' },
              { label: event.category, to: `/events?cat=${event.categoryId}` },
              { label: '事件详情' },
            ]}
          />
          <article className="mt-5 max-w-4xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-price-tag-3-line text-sm leading-none" aria-hidden="true"></i>
              {event.category}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {event.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-building-2-line text-base leading-none" aria-hidden="true"></i>
                {event.campus}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-base leading-none" aria-hidden="true"></i>
                {event.publishedAt}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-base leading-none" aria-hidden="true"></i>
                阅读约 {event.readTime}
              </span>
              <span className="flex items-center gap-1.5 text-primary-600">
                <i className="ri-fire-fill text-base leading-none" aria-hidden="true"></i>
                热度 {event.heat.toLocaleString()}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line text-base leading-none" aria-hidden="true"></i>
                讨论 {event.comments}
              </span>
            </div>
          </article>
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:gap-14">
            <div className="min-w-0">
              <div className="relative h-60 w-full overflow-hidden rounded-lg bg-background-200 md:h-96">
                <img
                  src={event.cover}
                  alt={`${event.title} 现场配图`}
                  title={`${event.title} 高校吃瓜事件`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-8 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5 md:p-6">
                <p className="text-xs font-semibold tracking-widest text-primary-700">事件速览</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-800 md:text-base">{event.summary}</p>
              </div>

              <div className="mt-10">
                <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                  <i className="ri-history-line text-xl leading-none text-primary-500" aria-hidden="true"></i>
                  事件时间线
                </h2>
                <ol className="mt-6 space-y-0">
                  {event.timeline.map((step, index) => (
                    <li key={`${step.time}-${index}`} className="relative flex gap-4 pb-6 last:pb-0">
                      <span className="relative flex flex-col items-center">
                        <span
                          className={`z-10 h-3 w-3 shrink-0 rounded-full ${
                            index === 0 ? 'bg-primary-500' : 'bg-accent-500'
                          }`}
                        ></span>
                        {index !== event.timeline.length - 1 && (
                          <span className="mt-1 w-px flex-1 bg-background-300"></span>
                        )}
                      </span>
                      <span className="flex-1 pb-1">
                        <span className="block text-xs font-semibold tracking-wide text-primary-600">{step.time}</span>
                        <span className="mt-1 block text-sm leading-relaxed text-foreground-700">{step.text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="mt-10 space-y-5">
                {event.content.map((block, index) => {
                  if (block.type === 'heading') {
                    return (
                      <h2
                        key={index}
                        className="pt-2 font-heading text-lg font-black text-foreground-950 md:text-xl"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === 'quote') {
                    return (
                      <blockquote
                        key={index}
                        className="rounded-lg bg-background-100 px-5 py-4 font-heading text-sm italic leading-relaxed text-foreground-800 md:text-base"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  if (block.type === 'list') {
                    return (
                      <ul key={index} className="space-y-2.5">
                        {block.items?.map((item) => (
                          <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-foreground-700 md:text-base">
                            <i
                              className="ri-checkbox-blank-circle-fill mt-2 text-[6px] leading-none text-primary-400"
                              aria-hidden="true"
                            ></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={index} className="text-sm leading-relaxed text-foreground-700 md:text-base">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm font-medium text-foreground-500">相关标签</span>
                {event.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/events?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-fire-line text-lg leading-none text-primary-500" aria-hidden="true"></i>
                  当前热榜
                </h3>
                <ol className="mt-4 space-y-3">
                  {topHot.map((item, index) => (
                    <li key={item.id}>
                      <Link to={`/events/${item.id}`} className="group flex gap-3">
                        <span
                          className={`font-heading text-sm font-black ${
                            index < 3 ? 'text-primary-500' : 'text-foreground-300'
                          }`}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="flex-1">
                          <span className="line-clamp-2 block text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-400">
                            {item.heat.toLocaleString()} 热度
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg bg-foreground-950 p-5 text-background-100">
                <h3 className="font-heading text-base font-bold text-background-50">有后续想补充？</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-200">
                  如果你了解这起事件的最新进展，欢迎投稿，我们会核对后更新到时间线。
                </p>
                <Link
                  to="/#submit"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-send-plane-line text-base leading-none" aria-hidden="true"></i>
                  去投稿
                </Link>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">按分类继续逛</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/events"
                    className="rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-200"
                  >
                    全部事件
                  </Link>
                  <Link
                    to={`/events?cat=${event.categoryId}`}
                    className="rounded-full bg-primary-100 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                  >
                    {event.category}
                  </Link>
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-14 border-t border-background-200 pt-10 md:mt-20">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">你可能还想围观</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <EventCard key={item.id} event={item} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}